<script lang="ts">
  import { onMount, tick } from "svelte";
  import type { Endpoint } from "./interfaces/endpoint";
  import type { ChildExplore, EventGroup, Explore, Group } from "./interfaces/explore";
  import { Api, configuration } from "./services/api";
  import { getBrowser, getHash, setHash, sort } from "./services/shared";
  import EndpointGroup from "./components/endpoint-group.svelte";
  import Header from "./components/header.svelte";
  import Navigation from "./components/navigation.svelte";

  const api = new Api();
  const browser = getBrowser();

  const lazyLoadOffsetScroll = 1800;
  const lazyLoadOffsetJump = 318;

  let anchor: string | null = getHash();
  $: {
    if (preventSetScrollPositionOnAnchorChange) {
      preventSetScrollPositionOnAnchorChange = false;
    } else {
      adjustedScrollTop = setScrollPosition(anchor, lazyload);
    }
  }

  let preventLazyLoadOnMount = false;
  let preventSetScrollPositionOnAnchorChange = false;
  let preventOnScroll = false;
  let hasScrolledDown = false;
  let hasJumpedToPosition = false;
  let endpointIsNotLoadedYet = false;
  let endpointGroupRendered = false;
  let endpointRendered = false;
  let resizeTimeout: any = null;

  let currentScrollTop = 0;
  let previousScrollTop = 0;
  let adjustedScrollTop = 0;

  let headerContainer: Element | null = null; 
  let groupsContainer: Element | null = null;
  let endpointsContainers: Element[] = [];

  let explore: Explore | null = null;
  let groups: Group[] = [];
  const loadingEndpointGroupIds: string[] = [];
  let eventGroups: EventGroup[] = [];
  let endpointGroups: ChildExplore[] = [];

  const loadingEndpointIds: string[] = [];
  let endpoints: { parent: string; endpoints: Endpoint[] }[] = [];

  const getScrollYForAnchor = (newAnchor: string) => {
    let scrollY = 0;

    if (newAnchor) {
      scrollY += headerContainer?.getBoundingClientRect().height ?? 0;
      const anchorFragments = newAnchor?.split('/');
      const foundChildIndex = groups?.findIndex(g => g.name === anchorFragments[0]);

      if (foundChildIndex > -1) {
        const groupsContainerChildren = groupsContainer?.children;
        if (groupsContainerChildren?.length) {
          // calculates scroll distance to the group
          for (let i = 0; i < foundChildIndex; i++) {
            scrollY += groupsContainerChildren[i].getBoundingClientRect().height;
          }

          // calculates scroll distance to the endpoint section inside the group
          const foundEndpointIndex = endpointGroups.find(g => g.name === anchorFragments[0])?.groups.findIndex(eg => eg.name === anchorFragments[1]);
          if (foundEndpointIndex > -1) {
            for (let i = 0; i < foundEndpointIndex; i++) {
              scrollY += endpointsContainers[foundChildIndex].children[i].getBoundingClientRect().height;
            }
            scrollY += groupsContainerChildren[foundChildIndex].children[0].getBoundingClientRect().height;
          }
        }
      }
    }

    return scrollY;
  };

  const setScrollPosition = (newAnchor: string | null, callback = () => null) => {
    preventOnScroll = true;

    const scrollY = getScrollYForAnchor(newAnchor);

    if (newAnchor) {
      const anchorFragments = newAnchor?.split('/');
      const foundEndpointIndex = endpointGroups.find(g => g.name === anchorFragments[0])?.groups.findIndex(eg => eg.name === anchorFragments[1]);

      if (anchorFragments[1] && foundEndpointIndex === undefined) {
        endpointIsNotLoadedYet = true;
      }
    } else if (!scrollY) {
      anchor = null;
    }

    window.scrollTo(0, scrollY);
    preventLazyLoadOnMount = true;

    callback();

    return scrollY;
  };

  const setAnchors = () => {
    const activeChildIndex = [...(groupsContainer ? groupsContainer.children : [])]?.findIndex(gc => {
      const itemRect = gc.getBoundingClientRect();
      const flooredTop = Math.floor(itemRect.top);
      return Math.floor(itemRect.bottom) > 0 && flooredTop <= 0 && flooredTop <= window.innerHeight;
    });

    const activeEndpointIndex = [...(endpointsContainers[activeChildIndex] ? endpointsContainers[activeChildIndex].children : [])]?.findIndex(ec => {
      const itemRect = ec.getBoundingClientRect();
      const flooredTop = Math.floor(itemRect.top);
      return Math.floor(itemRect.bottom) > 0 && flooredTop <= 0 && flooredTop <= window.innerHeight;
    });

    const itemName = groups[activeChildIndex]?.name;
    const endpointName = endpointGroups.find(e => e.name === itemName)?.groups[activeEndpointIndex]?.name;
    const newAnchor = itemName ? itemName + (endpointName ? ('/' + endpointName) : '') : null;

    if ((newAnchor !== anchor)) {
      preventSetScrollPositionOnAnchorChange = true;
      anchor = newAnchor;
      setHash(anchor, true);
    }
  };

  const getDataWithSortedGroups = async (config = {}) => {
    const response = await api.getPromiseConfig(config);
    const groups = sort('alphabetically', response.groups, 'name');
    const generalInformationIndex = groups.findIndex(({ name }) => name === 'general Information');
    const errorHandlingIndex = groups.findIndex(({ name }) => name === 'ErrorHandling');

    if (generalInformationIndex > -1) {
      const generalInformation = groups[generalInformationIndex];
      groups.splice(generalInformationIndex, 1);
      groups.unshift(generalInformation);
    }
    
    if (errorHandlingIndex > -1) {
      const errorHandling = groups[errorHandlingIndex];
      groups.splice(errorHandlingIndex, 1);
      groups.push(errorHandling);
    }

    return { ...response, groups };

  };

  const lazyloadHeaderData = async (shouldLoadContentBelow: boolean) => {
    if (headerContainer) {
      const headerElementRect = headerContainer.getBoundingClientRect();
      const group = groups[0] ?? null;
      if (
        group &&
        headerElementRect.bottom > (shouldLoadContentBelow ? (hasJumpedToPosition && !endpointIsNotLoadedYet ? -lazyLoadOffsetJump : 0) : -lazyLoadOffsetScroll) &&
        headerElementRect.top <= (window.innerHeight + (shouldLoadContentBelow ? (hasJumpedToPosition ? lazyLoadOffsetJump : lazyLoadOffsetScroll) : 0))
      ) {
        if (!loadingEndpointGroupIds.includes(groups[0].link)) {
          loadingEndpointGroupIds.push(groups[0].link);
          let eventGroup: EventGroup | null = null;
          if (!configuration && group.eventLink) { eventGroup = await api.getPromiseConfig({ mainPath: group.eventLink }); }
          const endpointGroup = await getDataWithSortedGroups({ mainPath: group.link });
          eventGroups = eventGroup ? [...eventGroups, eventGroup] : [...eventGroups];
          endpointGroups = [...endpointGroups, endpointGroup];

          await tick();
          preventOnScroll = true;
        }
      }
    }
  };

  const scrollToEndpointAndLazyLoad = async (scrollTop: number) => {
    if (
      adjustedScrollTop === 0 && scrollTop === 0 ||
      Math.round(adjustedScrollTop) !== Math.round(scrollTop)
    ) {
      await tick();
      adjustedScrollTop = setScrollPosition(anchor, lazyload);
    }
  };

  const adjustScrollPosition = () => {
    if (hasJumpedToPosition) {
      const roundedTop = Math.round(currentScrollTop);
      const roundedScrollY = Math.round(getScrollYForAnchor(anchor));

      if (roundedTop + 4 >= roundedScrollY && roundedTop - 4 <= roundedScrollY) {
        preventOnScroll = true;
      } else {
        adjustedScrollTop = setScrollPosition(anchor);
      }
    }
  };

  const lazyload = async () => {
    hasJumpedToPosition = preventOnScroll;
    const shouldLoadContentBelow = hasJumpedToPosition || hasScrolledDown;
    const savedCurrentScrollTop = currentScrollTop;
    const endpointGroupsElements = [...(groupsContainer ? groupsContainer.children : [])];
    for (
      let iGroup = (shouldLoadContentBelow ? 0 : (endpointGroupsElements?.length ?? 1) - 1);
      shouldLoadContentBelow ? iGroup < (endpointGroupsElements?.length ?? 0) : iGroup >= 0;
      shouldLoadContentBelow ? iGroup++ : iGroup--
    ) {
      const endpointGroupElementRect = endpointGroupsElements[iGroup].getBoundingClientRect();
      const group = groups[iGroup];

      if (
        endpointGroupElementRect.bottom > (shouldLoadContentBelow ? (hasJumpedToPosition && !endpointIsNotLoadedYet ? -lazyLoadOffsetJump : 4) : -lazyLoadOffsetScroll) &&
        endpointGroupElementRect.top <= (window.innerHeight + (shouldLoadContentBelow ? (hasJumpedToPosition ? lazyLoadOffsetJump : lazyLoadOffsetScroll) : 0))
      ) {
        let endpointGroup = endpointGroups?.find(eg => eg.name === group.name);

        if (group.link && !loadingEndpointGroupIds.includes(group.link)) {
          loadingEndpointGroupIds.push(group.link);
          let eventGroup: EventGroup | null = null;
          if (!configuration && group.eventLink) { eventGroup = await api.getPromiseConfig({ mainPath: group.eventLink }); }
          endpointGroup = await getDataWithSortedGroups({ mainPath: group.link });
          eventGroups = eventGroup ? [...eventGroups, eventGroup] : [...eventGroups];
          endpointGroups = [...endpointGroups, endpointGroup];

          await tick();
          endpointGroupRendered = true;
          setScrollPosition(anchor);

          if ((browser === 'firefox' || browser === 'safari') && !shouldLoadContentBelow && !hasJumpedToPosition) {
            reduceScrollJumping(
              endpointGroupElementRect.height,
              endpointGroupsElements[iGroup].getBoundingClientRect().height
            );
          }

          preventOnScroll = true;
        }

        const endpointElements = [...endpointsContainers[iGroup].children];
        const shouldLoadEndpointFromBelow = (hasJumpedToPosition ? endpointGroupElementRect.bottom > 4 : shouldLoadContentBelow);
        for (
          let iEndpoint = (shouldLoadEndpointFromBelow ? 0 : (endpointElements?.length ?? 1) - 1);
          shouldLoadEndpointFromBelow ? iEndpoint < (endpointElements?.length ?? 0) : iEndpoint >= 0;
          shouldLoadEndpointFromBelow ? iEndpoint++ : iEndpoint--
        ) {
          const endpointExploreLink = endpointGroup?.groups[iEndpoint].links[1]?.link;
          const endpointElementRect = endpointElements[iEndpoint].getBoundingClientRect();

          if (
            endpointExploreLink &&
            !endpointIsNotLoadedYet &&
            !loadingEndpointIds.includes(endpointExploreLink) &&
            endpointElementRect.bottom > (shouldLoadContentBelow ? (hasJumpedToPosition ? -lazyLoadOffsetJump : 0) : -lazyLoadOffsetScroll) &&
            endpointElementRect.top <= (window.innerHeight + (shouldLoadContentBelow ? (hasJumpedToPosition ? lazyLoadOffsetJump : lazyLoadOffsetScroll) : 0))
          ) {
            loadingEndpointIds.push(endpointExploreLink);
            adjustScrollPosition();
            const endpoint = await api.getPromiseConfig({ mainPath: endpointExploreLink });

            const existingEndpointsParent = endpoints.find(e => e.parent === group.name);
            if (existingEndpointsParent) {
              existingEndpointsParent.endpoints.push(endpoint);
              endpoints = [...endpoints];
            } else {
              endpoints = [...endpoints, { parent: group.name, endpoints: [endpoint] }];
            }

            await tick();
            endpointRendered = true;
            adjustScrollPosition();

            if ((browser === 'firefox' || browser === 'safari') && !shouldLoadContentBelow && !hasJumpedToPosition) {
              reduceScrollJumping(
                endpointElementRect.height,
                endpointElements[iEndpoint].getBoundingClientRect().height
              );
            }
          }
        }

        if (endpointIsNotLoadedYet && group.name === anchor?.split('/')[0]) {
          scrollToEndpointAndLazyLoad(savedCurrentScrollTop);
          endpointIsNotLoadedYet = false;
          break;
        }
      }

      if (endpointIsNotLoadedYet && group.name === anchor?.split('/')[0]) {
        break;
      }
    }

    lazyloadHeaderData(shouldLoadContentBelow);

    if (hasJumpedToPosition) {
      adjustedScrollTop = 0;
      hasJumpedToPosition = false;
    }
  };

  const reduceScrollJumping = (previousElementHeight: number, currentElementHeight: number) => {
    preventOnScroll = true;
    window.scrollTo(0, currentScrollTop + currentElementHeight - previousElementHeight);
}

  const onScroll = () => {
    currentScrollTop = document.getElementsByTagName('html')[0]?.scrollTop;

    if (endpointGroupRendered) {
      endpointGroupRendered = false;
    } else if (endpointRendered) {
      endpointRendered = false;
    } else {
      hasScrolledDown = currentScrollTop >= previousScrollTop;
    }

    if (preventOnScroll) {
      preventOnScroll = false;

      if (hasJumpedToPosition && Math.round(currentScrollTop) === Math.round(getScrollYForAnchor(anchor))) {
        preventOnScroll = true;
      }
    } else {
      if (endpointIsNotLoadedYet) {
        endpointIsNotLoadedYet = false;
      } else {
        setAnchors();
      }

      lazyload();
    }

    previousScrollTop = currentScrollTop;
  };

  const requestGroups = async (initialAnchor: string) => {
    explore = await getDataWithSortedGroups({ path: 'explore' });
    groups = explore.groups;
    await tick();
    adjustedScrollTop = setScrollPosition(initialAnchor, lazyload);
    document.addEventListener('scroll', onScroll, { passive: true });

    if (!preventLazyLoadOnMount) {
      lazyload();
    }
  };

  const onResize = () => {
    if (resizeTimeout) { clearTimeout(resizeTimeout); }
    resizeTimeout = setTimeout(() => tick().then(() => {
      setAnchors();
      lazyload();
    }), 200);
  };

  const resetView = async () => {
    preventOnScroll = true;
    eventGroups = [];
    endpointGroups = [];
    await tick();
    setScrollPosition(anchor);
  };

  const setEndpointsContainer = (endpointsContainer: Element) => {
    endpointsContainers.push(endpointsContainer);
  };

  const toggledExpandEndpointGroup = async (group: Group) => {
    if (!loadingEndpointGroupIds.includes(group.link)) {
      loadingEndpointGroupIds.push(group.link);
      let eventGroup: EventGroup | null = null;
      if (!configuration && group.eventLink) { eventGroup = await api.getPromiseConfig({ mainPath: group.eventLink }); }
      const endpointGroup = await getDataWithSortedGroups({ mainPath: group.link });
      eventGroups = eventGroup ? [...eventGroups, eventGroup] : [...eventGroups];
      endpointGroups = [...endpointGroups, endpointGroup];
    }
  };

  onMount(() => {
    requestGroups(anchor);
    window.addEventListener('hashchange', () => anchor = getHash(), { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('unload', resetView, { passive: true });
  });
</script>

<header class="appHeader" bind:this={headerContainer}>
  <Header
    {explore}
    eventGroup={eventGroups[0] ?? null}
  />
</header>

<div class="groupsContainer" bind:this={groupsContainer}>
  {#each groups as group}
  <div class="groupsContainer__item">
    <EndpointGroup
      bind:group={group}
      endpointGroup={endpointGroups.find(eg => eg.name === group.name)?.groups ?? []}
      eventGroup={eventGroups.find(eg => eg.key === group.name)}
      endpoints={endpoints.find(e => e.parent === group.name)?.endpoints ?? []}
      {setEndpointsContainer}
    />
  </div>
  {/each}
</div>

<Navigation
  bind:anchor={anchor}
  {explore}
  {groups}
  {endpointGroups}
  {setScrollPosition}
  {toggledExpandEndpointGroup}
/>
